import type { DateTime } from "luxon";
import { formatPersonFullName, getNurseEffectiveStatus } from "../../utils/formatters";
import { EffectiveEmployeeStatus, type Nurse, type NurseFullRole } from "../../utils/models";
import { isAgencyNurse, normalizeUnionName } from "../../utils/utilFunctions";
import { matchesContractSections, TEAM_SECTIONS } from "./config";

/**
 * The default status view. Kept here beside the other filter constants (rather than in the hook)
 * so the URL serializer and the hook agree on it without importing each other.
 */
export const DEFAULT_SELECTED_STATUSES: readonly string[] = [EffectiveEmployeeStatus.Active];

/** Sentinel option id for "staff with no job assigned" in the Jobs facet group. */
export const NO_JOB_FILTER_ID = "__no_job__";
/** Sentinel option id for "staff with no integration source" in the Integrator facet group. */
export const NO_INTEGRATOR_FILTER_ID = "__no_integrator__";
/** Sentinel option id for "staff without an agency" (non-agency staff) in the Agency facet group. */
export const NO_AGENCY_FILTER_ID = "__no_agency__";
/** Sentinel bucket for agency staff whose `agencyName` is blank — keeps them selectable/countable. */
export const UNNAMED_AGENCY_FILTER_ID = "__unnamed_agency__";
/** Sentinel option id for "non-union staff" (null/blank `union`) in the Union facet group. */
export const NO_UNION_FILTER_ID = "__no_union__";
// Email / Phone are independent facet dimensions (own groups), each with a has/none option.
// Because groups AND together, checking "No email" + "No phone" yields staff missing BOTH,
// and "Has email" + "Has phone" yields staff with both.
export const HAS_EMAIL_FILTER_ID = "__has_email__";
export const NO_EMAIL_FILTER_ID = "__no_email__";
export const HAS_PHONE_FILTER_ID = "__has_phone__";
export const NO_PHONE_FILTER_ID = "__no_phone__";
// Auto-approval eligibility facet (feature-flagged): eligible vs. non-eligible, OR'ed like the others.
export const AUTO_APPROVE_ELIGIBLE_ID = "__auto_approve_eligible__";
export const AUTO_APPROVE_NON_ELIGIBLE_ID = "__auto_approve_non_eligible__";

/**
 * Last-login buckets, ORDERED STALEST FIRST.
 *
 * The question anyone actually asks of this column is "who has gone quiet" — never "who logged in
 * today" — so `Never` leads and the recency buckets trail. Plain readable ids rather than `__x__`
 * sentinels because these travel in the URL.
 */
export const LAST_LOGIN_NEVER_ID = "never";
export const LAST_LOGIN_OVER_90_ID = "over-90-days";
export const LAST_LOGIN_31_90_ID = "31-90-days";
export const LAST_LOGIN_8_30_ID = "8-30-days";
export const LAST_LOGIN_PAST_7_ID = "past-7-days";
export const LAST_LOGIN_BUCKET_IDS = [LAST_LOGIN_NEVER_ID, LAST_LOGIN_OVER_90_ID, LAST_LOGIN_31_90_ID, LAST_LOGIN_8_30_ID, LAST_LOGIN_PAST_7_ID] as const;

/** Upper bound (days since last login, exclusive) for each non-`Never` bucket, stalest first. */
const LAST_LOGIN_BUCKET_MAX_DAYS: ReadonlyArray<readonly [string, number]> = [
	[LAST_LOGIN_PAST_7_ID, 7],
	[LAST_LOGIN_8_30_ID, 30],
	[LAST_LOGIN_31_90_ID, 90],
];

/**
 * Which last-login bucket a nurse falls in, as of `now`.
 *
 * `Never` covers two different realities that look identical here — a nurse who has an app account
 * and has never opened it, and a nurse with no `user` row at all (whose `lastLoginTime` can never
 * be anything else). Both are "has not used the app", which is the question this facet answers.
 */
export function nurseLastLoginBucket(nurse: Nurse, now: DateTime): string {
	if (!nurse.lastLoginTime) return LAST_LOGIN_NEVER_ID;
	const days = now.diff(nurse.lastLoginTime, "days").days;
	for (const [bucketId, maxDays] of LAST_LOGIN_BUCKET_MAX_DAYS) {
		if (days <= maxDays) return bucketId;
	}
	return LAST_LOGIN_OVER_90_ID;
}

/** OR'ed Last-login selection: empty = unrestricted. */
export function matchesLastLoginSelection(nurse: Nurse, selectedLastLogin: ReadonlySet<string>, now: DateTime): boolean {
	if (selectedLastLogin.size === 0) return true;
	return selectedLastLogin.has(nurseLastLoginBucket(nurse, now));
}

/**
 * The Team page's client-side filter state. Single source of truth for BOTH the
 * table's row predicate (`filterTeamNurses`) and the sidebar facet counts
 * (`computeTeamFacetCounts`) so a facet count always equals the rows you'd see.
 */
export type TeamFilterState = {
	/**
	 * Free-text search over name, email and phone. Empty = no search.
	 *
	 * A non-empty query BYPASSES every facet (see `buildTeamPredicates`): searching someone by name
	 * has to find them whatever the rail is set to, because the alternative is a roster that swears
	 * an employee doesn't exist when they are merely inactive, or agency, or on another contract.
	 */
	searchQuery: string;
	/** Empty/undefined = no role filter. */
	selectedRoles: NurseFullRole[] | undefined;
	/** Job identity is the job `name`; may include NO_JOB_FILTER_ID. Empty = no job filter. */
	selectedJobNames: string[];
	/** Contract Type section titles (see TEAM_SECTIONS). Empty = no filter. */
	selectedSections: ReadonlySet<string>;
	/** EffectiveEmployeeStatus values, OR'ed. Empty = all statuses. The default view = {Active}. */
	selectedStatuses: ReadonlySet<string>;
	/** ExternalSource values (+ NO_INTEGRATOR_FILTER_ID), OR'ed. Empty = no integrator filter. */
	selectedIntegrators: ReadonlySet<string>;
	/**
	 * Agency names (+ NO_AGENCY_FILTER_ID / UNNAMED_AGENCY_FILTER_ID), OR'ed. Empty = no
	 * agency restriction (agency staff show). The default view = {NO_AGENCY_FILTER_ID},
	 * preserving the historical "hide agency staff by default" behavior.
	 */
	selectedAgencies: ReadonlySet<string>;
	/** Union names (+ NO_UNION_FILTER_ID), OR'ed. Empty = no union restriction (the default). */
	selectedUnions: ReadonlySet<string>;
	/** Email dimension (HAS_EMAIL/NO_EMAIL), OR'ed. Empty = no email restriction. */
	selectedEmail: ReadonlySet<string>;
	/** Phone dimension (HAS_PHONE/NO_PHONE), OR'ed. Empty = no phone restriction. */
	selectedPhone: ReadonlySet<string>;
	/** Auto-approval dimension (ELIGIBLE/NON_ELIGIBLE), OR'ed. Empty = no auto-approval restriction. */
	selectedAutoApprove: ReadonlySet<string>;
	/** Last-login buckets (see LAST_LOGIN_BUCKET_IDS), OR'ed. Empty = no last-login restriction. */
	selectedLastLogin: ReadonlySet<string>;
	/** When on, only nurses with a pending HRIS status decision show — bypassing status/stat filters. */
	showOnlyDecisions: boolean;
};

/** Auto-approval eligibility defaults to true — only an explicit `false` is "non-eligible". */
export function isAutoApproveEligible(nurse: Nurse): boolean {
	return nurse.autoApproveEligible !== false;
}

/** True when the nurse has no email on file (empty/blank). */
export function isMissingEmail(nurse: Nurse): boolean {
	return !nurse.email?.trim();
}

/** True when the nurse has no phone number on file (null/undefined/blank). */
export function isMissingPhone(nurse: Nurse): boolean {
	return !nurse.phoneNumber?.trim();
}

/** Has/None selection for a boolean-gap dimension. Empty = unrestricted; both options = everyone. */
function matchesGapDimension(missing: boolean, selected: ReadonlySet<string>, hasId: string, noId: string): boolean {
	if (selected.size === 0) return true;
	return (selected.has(hasId) && !missing) || (selected.has(noId) && missing);
}

/** OR'ed Email dimension: empty = unrestricted; HAS_EMAIL = with email; NO_EMAIL = without. */
export function matchesEmailSelection(nurse: Nurse, selectedEmail: ReadonlySet<string>): boolean {
	return matchesGapDimension(isMissingEmail(nurse), selectedEmail, HAS_EMAIL_FILTER_ID, NO_EMAIL_FILTER_ID);
}

/** OR'ed Phone dimension: empty = unrestricted; HAS_PHONE = with phone; NO_PHONE = without. */
export function matchesPhoneSelection(nurse: Nurse, selectedPhone: ReadonlySet<string>): boolean {
	return matchesGapDimension(isMissingPhone(nurse), selectedPhone, HAS_PHONE_FILTER_ID, NO_PHONE_FILTER_ID);
}

/** OR'ed Auto-approval dimension: empty = unrestricted; ELIGIBLE = eligible; NON_ELIGIBLE = not. */
export function matchesAutoApproveSelection(nurse: Nurse, selectedAutoApprove: ReadonlySet<string>): boolean {
	// `matchesGapDimension` treats its boolean as the "negative" bucket, so pass the non-eligible flag.
	return matchesGapDimension(!isAutoApproveEligible(nurse), selectedAutoApprove, AUTO_APPROVE_ELIGIBLE_ID, AUTO_APPROVE_NON_ELIGIBLE_ID);
}

export type TeamFilterContext = {
	departmentId: string;
	/** Job names enabled for this department; dropdown facets must not source from historical nurse jobs. */
	enabledJobNames: ReadonlySet<string>;
	/** Nurse ids with a pending HRIS status decision. */
	decisionNurseIds: ReadonlySet<number>;
	/** Tri-state stat-card filter resolved to a predicate; null when no stat card is active. */
	statFilterPredicate: ((nurse: Nurse) => boolean) | null;
	/** "Now" for the last-login buckets. Injected rather than read from the clock so buckets are testable. */
	now: DateTime;
};

/**
 * Per-option counts over the ENTIRE unfiltered roster. Drives the two-tier option
 * visibility rule: baseline 0 → the option doesn't exist in this facility and is
 * not rendered at all; baseline > 0 with a current facet count of 0 → rendered
 * greyed/disabled.
 */
export type TeamFacetBaselines = {
	roles: ReadonlyMap<string, number>;
	jobs: ReadonlyMap<string, number>;
	noJob: number;
	contract: ReadonlyMap<string, number>;
	statuses: ReadonlyMap<string, number>;
	integrators: ReadonlyMap<string, number>;
	noIntegrator: number;
	/** Keyed by agency key (agencyName or UNNAMED_AGENCY_FILTER_ID). */
	agencies: ReadonlyMap<string, number>;
	noAgency: number;
	/** Keyed by `nurseUnionKey` (trimmed + case-folded). */
	unions: ReadonlyMap<string, number>;
	/**
	 * The spelling to DISPLAY for each `unions` key, since the key itself is case-folded.
	 *
	 * One entry per key — every counted key is tallied a spelling in the same pass — so an option
	 * can never render unlabelled. Where a department holds several spellings of one union, the one
	 * the most rows use wins; see `resolveUnionLabels`.
	 */
	unionLabels: ReadonlyMap<string, string>;
	noUnion: number;
	/** Keyed by last-login bucket id. */
	lastLogin: ReadonlyMap<string, number>;
};

export type TeamFacetCounts = {
	/** Keyed by NurseFullRole. */
	roles: ReadonlyMap<string, number>;
	/** Keyed by job name. */
	jobs: ReadonlyMap<string, number>;
	/** Staff with no job assigned (the NO_JOB_FILTER_ID option). */
	noJob: number;
	/** Keyed by TEAM_SECTIONS title. */
	contract: ReadonlyMap<string, number>;
	/** Keyed by EffectiveEmployeeStatus value. */
	statuses: ReadonlyMap<string, number>;
	/** Keyed by ExternalSource value. */
	integrators: ReadonlyMap<string, number>;
	/** Staff with no integration source (the NO_INTEGRATOR_FILTER_ID option). */
	noIntegrator: number;
	/** Keyed by agency key (agencyName or UNNAMED_AGENCY_FILTER_ID). */
	agencies: ReadonlyMap<string, number>;
	/** Non-agency staff (the NO_AGENCY_FILTER_ID option). */
	noAgency: number;
	/** Keyed by trimmed union name. */
	unions: ReadonlyMap<string, number>;
	/** Non-union staff (the NO_UNION_FILTER_ID option). */
	noUnion: number;
	/** Email dimension counts: staff with / without an email. */
	hasEmail: number;
	noEmail: number;
	/** Phone dimension counts: staff with / without a phone number. */
	hasPhone: number;
	noPhone: number;
	/** Keyed by last-login bucket id (see LAST_LOGIN_BUCKET_IDS). */
	lastLogin: ReadonlyMap<string, number>;
	/** Auto-approval dimension counts: eligible / non-eligible staff. */
	autoApproveEligible: number;
	autoApproveNonEligible: number;
	needsDecision: number;
	baselines: TeamFacetBaselines;
};

/** The nurse's integration sources (deduped); empty = not synced from any integrator. */
function nurseIntegrationSources(nurse: Nurse): string[] {
	const sources = (nurse.externalEmployees ?? []).map((e) => e.source).filter(Boolean);
	return [...new Set(sources)];
}

/**
 * The Agency facet key for an agency nurse (`isAgencyNurse` = Travel/PerDiem — the
 * same predicate the roster has always used). Blank agency names bucket under a
 * sentinel so those staff stay selectable and counts stay equal to rows.
 */
export function nurseAgencyKey(nurse: Nurse): string {
	return nurse.agencyName?.trim() || UNNAMED_AGENCY_FILTER_ID;
}

/** OR'ed Agency selection: empty = unrestricted; NO_AGENCY = non-agency staff. */
export function matchesAgencySelection(nurse: Nurse, selectedAgencies: ReadonlySet<string>): boolean {
	if (selectedAgencies.size === 0) return true;
	if (!isAgencyNurse(nurse.employmentType)) return selectedAgencies.has(NO_AGENCY_FILTER_ID);
	return selectedAgencies.has(nurseAgencyKey(nurse));
}

/**
 * The Union facet key: the trimmed, CASE-FOLDED union name, or null for non-union staff.
 * Null AND blank both read as non-union — the profile save path writes "Union" when a
 * member's union name is unknown, so a stored blank carries no membership signal.
 *
 * Case is folded because `nurse.union` is free text with several writers, so one union arrives
 * spelled "Union", "union" and " UNION " and the facet offered it as three checkboxes with three
 * partial counts. Folding is safe HERE and nowhere upstream: a facet key is a display grouping that
 * is never written back to a nurse, whereas the stored string is an exact overtime-config match key
 * (see `get_nurse_unions_in_department`). The spelling shown to the user comes from
 * `baselines.unionLabels`; this value only ever groups.
 */
export function nurseUnionKey(nurse: Nurse): string | null {
	return normalizeUnionName(nurse.union)?.toLowerCase() ?? null;
}

/** OR'ed Union selection: empty = unrestricted; NO_UNION = non-union staff. */
export function matchesUnionSelection(nurse: Nurse, selectedUnions: ReadonlySet<string>): boolean {
	if (selectedUnions.size === 0) return true;
	const unionKey = nurseUnionKey(nurse);
	if (unionKey === null) return selectedUnions.has(NO_UNION_FILTER_ID);
	return selectedUnions.has(unionKey);
}

type TeamPredicates = {
	matchesSearch: (nurse: Nurse) => boolean;
	matchesRoles: (nurse: Nurse) => boolean;
	matchesJobs: (nurse: Nurse) => boolean;
	matchesContract: (nurse: Nurse) => boolean;
	matchesIntegrators: (nurse: Nurse) => boolean;
	/** OR'ed Agency selection; empty = unrestricted. */
	matchesAgencies: (nurse: Nurse) => boolean;
	/** OR'ed Union selection; empty = unrestricted. */
	matchesUnions: (nurse: Nurse) => boolean;
	/** Has/None Email dimension; empty = unrestricted. */
	matchesEmail: (nurse: Nurse) => boolean;
	/** Has/None Phone dimension; empty = unrestricted. */
	matchesPhone: (nurse: Nurse) => boolean;
	/** OR'ed Auto-approval selection; empty = unrestricted. */
	matchesAutoApprove: (nurse: Nurse) => boolean;
	/** OR'ed Last-login buckets; empty = unrestricted. */
	matchesLastLogin: (nurse: Nurse) => boolean;
	/** OR'ed effective-status selection; empty = all. */
	matchesStatuses: (nurse: Nurse) => boolean;
	/** Statuses + stat-card filters — with the needs-decision bypass. */
	matchesStatusGroup: (nurse: Nurse) => boolean;
	/**
	 * `matchesStatusGroup` minus the status selection itself — the stat card alone, or decision
	 * membership in decisions mode.
	 *
	 * Exists for the status facets' own counts, which exclude their own OR'ed selection but keep
	 * everything else in the status area. Exported as a predicate rather than re-derived at the call
	 * site so the search bypass is written once; a second copy is exactly how the counts drifted from
	 * the rows before.
	 */
	matchesStatSegment: (nurse: Nurse) => boolean;
};

export function buildTeamPredicates(state: TeamFilterState, ctx: TeamFilterContext): TeamPredicates {
	const {
		searchQuery,
		selectedRoles,
		selectedJobNames,
		selectedSections,
		selectedStatuses,
		selectedIntegrators,
		selectedAgencies,
		selectedUnions,
		selectedEmail,
		selectedPhone,
		selectedAutoApprove,
		selectedLastLogin,
		showOnlyDecisions,
	} = state;
	const { departmentId, decisionNurseIds, enabledJobNames, statFilterPredicate, now } = ctx;

	const query = searchQuery.trim().toLowerCase();
	// A search overrides the rail rather than stacking with it. Every facet predicate below short-
	// circuits to true while a query is active, which keeps the row filter and the facet counts
	// agreeing on one rule instead of each implementing its own version of the bypass.
	const searchActive = query.length > 0;
	const matchesQuery = (nurse: Nurse) => {
		if (!searchActive) return true;
		// Both name orders, so the search matches whichever order the department displays (`name_format_last_first`).
		const haystack = [formatPersonFullName(nurse, false), formatPersonFullName(nurse, true), nurse.email, nurse.phoneNumber];
		return haystack.some((field) => field?.toLowerCase().includes(query));
	};

	// Agency staff resolve through the same per-department effective-status map as everyone
	// else (a nurse with no status entry at all reads as Inactive via the legacy boolean
	// fallback) — so with the default Status={Active} selection, checking an agency surfaces
	// its ACTIVE staff, and the agency option's count reflects exactly that.
	const matchesStatuses = (nurse: Nurse) => searchActive || selectedStatuses.size === 0 || selectedStatuses.has(getNurseEffectiveStatus(nurse, departmentId));

	return {
		matchesSearch: matchesQuery,
		matchesRoles: (nurse) => searchActive || !selectedRoles || selectedRoles.length === 0 || selectedRoles.includes(nurse.role),
		// Nurses with no jobs are excluded when a job filter is selected — unless the explicit
		// "No job assigned" option is checked (OR'ed with the concrete job options).
		matchesJobs: (nurse) => {
			if (searchActive || selectedJobNames.length === 0) return true;
			const jobNames = getEnabledNurseJobNames(nurse, enabledJobNames);
			if (jobNames.length === 0) return selectedJobNames.includes(NO_JOB_FILTER_ID);
			return jobNames.some((jobName) => selectedJobNames.includes(jobName));
		},
		matchesContract: (nurse) => searchActive || matchesContractSections(nurse.employmentType, selectedSections),
		matchesIntegrators: (nurse) => {
			if (searchActive || selectedIntegrators.size === 0) return true;
			const sources = nurseIntegrationSources(nurse);
			if (sources.length === 0) return selectedIntegrators.has(NO_INTEGRATOR_FILTER_ID);
			return sources.some((source) => selectedIntegrators.has(source));
		},
		matchesAgencies: (nurse) => searchActive || matchesAgencySelection(nurse, selectedAgencies),
		matchesUnions: (nurse) => searchActive || matchesUnionSelection(nurse, selectedUnions),
		matchesEmail: (nurse) => searchActive || matchesEmailSelection(nurse, selectedEmail),
		matchesPhone: (nurse) => searchActive || matchesPhoneSelection(nurse, selectedPhone),
		matchesAutoApprove: (nurse) => searchActive || matchesAutoApproveSelection(nurse, selectedAutoApprove),
		matchesLastLogin: (nurse) => searchActive || matchesLastLoginSelection(nurse, selectedLastLogin, now),
		matchesStatuses,
		matchesStatusGroup: (nurse) => {
			// A name search reaches inactive staff too — see `searchActive` above.
			if (searchActive) return true;
			// Needs-decision mode bypasses the status/stat filters entirely (a pending
			// decision must stay reachable even for inactive staff).
			if (showOnlyDecisions) return decisionNurseIds.has(nurse.id);
			if (!matchesStatuses(nurse)) return false;
			if (statFilterPredicate && !statFilterPredicate(nurse)) return false;
			return true;
		},
		matchesStatSegment: (nurse) => {
			if (searchActive) return true;
			if (showOnlyDecisions) return decisionNurseIds.has(nurse.id);
			return !statFilterPredicate || statFilterPredicate(nurse);
		},
	};
}

/** The table's row filter — every predicate applied. */
export function filterTeamNurses(nurses: readonly Nurse[], state: TeamFilterState, ctx: TeamFilterContext): Nurse[] {
	const p = buildTeamPredicates(state, ctx);
	return nurses.filter(
		(n) =>
			p.matchesSearch(n) &&
			p.matchesRoles(n) &&
			p.matchesJobs(n) &&
			p.matchesContract(n) &&
			p.matchesIntegrators(n) &&
			p.matchesAgencies(n) &&
			p.matchesUnions(n) &&
			p.matchesEmail(n) &&
			p.matchesPhone(n) &&
			p.matchesAutoApprove(n) &&
			p.matchesLastLogin(n) &&
			p.matchesStatusGroup(n),
	);
}

// Each employment type belongs to exactly one Contract Type section (FloatPool belongs to none).
const EMPLOYMENT_TYPE_TO_SECTION: ReadonlyMap<string, string> = new Map(
	TEAM_SECTIONS.flatMap((section) => section.employmentTypes.map((type) => [type, section.title] as const)),
);

const increment = (map: Map<string, number>, key: string) => map.set(key, (map.get(key) ?? 0) + 1);

function getEnabledNurseJobNames(nurse: Nurse, enabledJobNames: ReadonlySet<string>): string[] {
	// DEFAULT: Team filters treat missing nurse.jobs the same as no enabled jobs so the explicit
	// "No job assigned" facet remains the user's way to find those employees.
	return (nurse.jobs ?? []).map((job) => job.name).filter((name) => enabledJobNames.has(name));
}

/** One unconditional pass over the roster — which options exist in this facility at all. */
/**
 * One display spelling per folded union key: the one the most rows actually use.
 *
 * A department that writes "Union" forty times and "UNION" once means "Union" — so frequency, not
 * alphabetical order, is what picks the label. Ties break on code-unit order purely for
 * determinism, so the label never depends on roster ordering.
 */
function resolveUnionLabels(spellingsByKey: ReadonlyMap<string, ReadonlyMap<string, number>>): Map<string, string> {
	const labels = new Map<string, string>();
	for (const [key, spellings] of spellingsByKey) {
		let best: string | null = null;
		let bestCount = 0;
		for (const [spelling, count] of spellings) {
			if (count > bestCount || (count === bestCount && best !== null && spelling < best)) {
				best = spelling;
				bestCount = count;
			}
		}
		if (best !== null) labels.set(key, best);
	}
	return labels;
}

function computeBaselines(nurses: readonly Nurse[], departmentId: string, enabledJobNames: ReadonlySet<string>, now: DateTime): TeamFacetBaselines {
	const roles = new Map<string, number>();
	const jobs = new Map<string, number>();
	const contract = new Map<string, number>();
	const statuses = new Map<string, number>();
	const integrators = new Map<string, number>();
	const agencies = new Map<string, number>();
	const unions = new Map<string, number>();
	/** Per folded key, how often each stored spelling occurs — resolved to one label after the pass. */
	const unionSpellings = new Map<string, Map<string, number>>();
	const lastLogin = new Map<string, number>();
	let noJob = 0;
	let noIntegrator = 0;
	let noAgency = 0;
	let noUnion = 0;

	for (const nurse of nurses) {
		increment(roles, nurse.role);
		increment(lastLogin, nurseLastLoginBucket(nurse, now));
		increment(statuses, getNurseEffectiveStatus(nurse, departmentId));
		const section = EMPLOYMENT_TYPE_TO_SECTION.get(nurse.employmentType);
		if (section) increment(contract, section);
		const nurseJobNames = getEnabledNurseJobNames(nurse, enabledJobNames);
		if (nurseJobNames.length === 0) noJob++;
		for (const jobName of nurseJobNames) increment(jobs, jobName);
		const sources = nurseIntegrationSources(nurse);
		if (sources.length === 0) noIntegrator++;
		for (const source of sources) increment(integrators, source);
		if (isAgencyNurse(nurse.employmentType)) increment(agencies, nurseAgencyKey(nurse));
		else noAgency++;
		const unionKey = nurseUnionKey(nurse);
		if (unionKey === null) noUnion++;
		else {
			increment(unions, unionKey);
			// Tallied beside the count so every key is guaranteed a spelling to label it with.
			const spelling = normalizeUnionName(nurse.union);
			if (spelling !== null) {
				const spellings = unionSpellings.get(unionKey) ?? new Map<string, number>();
				increment(spellings, spelling);
				unionSpellings.set(unionKey, spellings);
			}
		}
	}

	return {
		roles,
		jobs,
		noJob,
		contract,
		statuses,
		integrators,
		noIntegrator,
		agencies,
		noAgency,
		unions,
		unionLabels: resolveUnionLabels(unionSpellings),
		noUnion,
		lastLogin,
	};
}

/**
 * Standard faceted-search counts ("exclude own group"): each option's count is the
 * number of nurses matching the search + stat filter + every OTHER group's active
 * selections + the option itself. Sibling counts within an OR'ed group
 * (roles / jobs / contract / statuses / integrators / agencies / unions) therefore
 * don't shrink when one option is checked.
 *
 * Exception — "Needs decision" is a full bypass of statuses/stat, mirroring the
 * row predicate; while it's ON it replaces those filters in every group's base.
 *
 * Also returns per-option BASELINES over the raw roster (no filters at all) for
 * the two-tier option-visibility rule.
 */
export function computeTeamFacetCounts(nurses: readonly Nurse[], state: TeamFilterState, ctx: TeamFilterContext): TeamFacetCounts {
	const p = buildTeamPredicates(state, ctx);
	const { decisionNurseIds, departmentId, enabledJobNames, now } = ctx;

	const roles = new Map<string, number>();
	const jobs = new Map<string, number>();
	const contract = new Map<string, number>();
	const statuses = new Map<string, number>();
	const integrators = new Map<string, number>();
	const agencies = new Map<string, number>();
	const unions = new Map<string, number>();
	const lastLogin = new Map<string, number>();
	let noJob = 0;
	let noIntegrator = 0;
	let noAgency = 0;
	let noUnion = 0;
	let hasEmail = 0;
	let noEmail = 0;
	let hasPhone = 0;
	let noPhone = 0;
	let autoApproveEligible = 0;
	let autoApproveNonEligible = 0;
	let needsDecision = 0;

	for (const nurse of nurses) {
		// Search is a global view toggle (not a facet), so it narrows EVERY group's counts uniformly.
		if (!p.matchesSearch(nurse)) continue;

		const byRoles = p.matchesRoles(nurse);
		const byJobs = p.matchesJobs(nurse);
		const byContract = p.matchesContract(nurse);
		const byIntegrators = p.matchesIntegrators(nurse);
		const byAgencies = p.matchesAgencies(nurse);
		const byUnions = p.matchesUnions(nurse);
		const byEmail = p.matchesEmail(nurse);
		const byPhone = p.matchesPhone(nurse);
		const byAutoApprove = p.matchesAutoApprove(nurse);
		const byLastLogin = p.matchesLastLogin(nurse);
		const hasDecision = decisionNurseIds.has(nurse.id);
		// The status area (statuses + stat) filters as one unit for the other groups; in
		// needs-decision mode it collapses to the decision membership (full bypass).
		//
		// Both of these come from the predicates rather than being rebuilt from `statFilterPredicate`
		// and `showOnlyDecisions` here. Rebuilding them dropped the search bypass that
		// `matchesStatusGroup` applies, so with a stat card active a search showed every match in the
		// table while every count stayed intersected with the segment — the exact disagreement the
		// "single source of truth" note on `TeamFilterState` promises cannot happen.
		const byStatusArea = p.matchesStatusGroup(nurse);
		const byStatSegment = p.matchesStatSegment(nurse);

		if (byJobs && byContract && byIntegrators && byAgencies && byUnions && byEmail && byPhone && byAutoApprove && byLastLogin && byStatusArea) {
			increment(roles, nurse.role);
		}
		if (byRoles && byContract && byIntegrators && byAgencies && byUnions && byEmail && byPhone && byAutoApprove && byLastLogin && byStatusArea) {
			const nurseJobNames = getEnabledNurseJobNames(nurse, enabledJobNames);
			if (nurseJobNames.length === 0) noJob++;
			for (const jobName of nurseJobNames) increment(jobs, jobName);
		}
		if (byRoles && byJobs && byIntegrators && byAgencies && byUnions && byEmail && byPhone && byAutoApprove && byLastLogin && byStatusArea) {
			const section = EMPLOYMENT_TYPE_TO_SECTION.get(nurse.employmentType);
			if (section) increment(contract, section);
		}
		if (byRoles && byJobs && byContract && byAgencies && byUnions && byEmail && byPhone && byAutoApprove && byLastLogin && byStatusArea) {
			const sources = nurseIntegrationSources(nurse);
			if (sources.length === 0) noIntegrator++;
			for (const source of sources) increment(integrators, source);
		}
		if (byRoles && byJobs && byContract && byIntegrators && byUnions && byEmail && byPhone && byAutoApprove && byLastLogin && byStatusArea) {
			// Agency group: standard exclude-own-group facet.
			if (isAgencyNurse(nurse.employmentType)) increment(agencies, nurseAgencyKey(nurse));
			else noAgency++;
		}
		if (byRoles && byJobs && byContract && byIntegrators && byAgencies && byEmail && byPhone && byAutoApprove && byLastLogin && byStatusArea) {
			// Union group: standard exclude-own-group facet.
			const unionKey = nurseUnionKey(nurse);
			if (unionKey === null) noUnion++;
			else increment(unions, unionKey);
		}
		if (byRoles && byJobs && byContract && byIntegrators && byAgencies && byUnions && byPhone && byAutoApprove && byLastLogin && byStatusArea) {
			// Email dimension: exclude its own selection, keep the other dimensions + groups.
			if (isMissingEmail(nurse)) noEmail++;
			else hasEmail++;
		}
		if (byRoles && byJobs && byContract && byIntegrators && byAgencies && byUnions && byEmail && byAutoApprove && byLastLogin && byStatusArea) {
			// Phone dimension: exclude its own selection.
			if (isMissingPhone(nurse)) noPhone++;
			else hasPhone++;
		}
		if (byRoles && byJobs && byContract && byIntegrators && byAgencies && byUnions && byEmail && byPhone && byLastLogin && byStatusArea) {
			// Auto-approval dimension: exclude its own selection, keep the other dimensions + groups.
			if (isAutoApproveEligible(nurse)) autoApproveEligible++;
			else autoApproveNonEligible++;
		}
		if (byRoles && byJobs && byContract && byIntegrators && byAgencies && byUnions && byEmail && byPhone && byAutoApprove && byStatusArea) {
			// Last-login group: standard exclude-own-group facet.
			increment(lastLogin, nurseLastLoginBucket(nurse, now));
		}
		if (byRoles && byJobs && byContract && byIntegrators && byAgencies && byUnions && byEmail && byPhone && byAutoApprove && byLastLogin) {
			// Status facets exclude their own OR'ed selection but keep the stat-card filter
			// (or the needs-decision bypass).
			if (byStatSegment) {
				increment(statuses, getNurseEffectiveStatus(nurse, departmentId));
			}
			// "Needs decision": full bypass of statuses/stat.
			if (hasDecision) needsDecision++;
		}
	}

	return {
		roles,
		jobs,
		noJob,
		contract,
		statuses,
		integrators,
		noIntegrator,
		agencies,
		noAgency,
		unions,
		noUnion,
		lastLogin,
		hasEmail,
		noEmail,
		hasPhone,
		noPhone,
		autoApproveEligible,
		autoApproveNonEligible,
		needsDecision,
		baselines: computeBaselines(nurses, departmentId, enabledJobNames, now),
	};
}
